import { Download, Copy as CopyIcon, Plus } from 'lucide-react';
import { useState } from 'react';
import { useStore } from '@/store';
import { CollapsibleSection } from '@/components/ui/CollapsibleSection';
import { Button } from '@/components/ui/Button';
import { ScreenEmpty } from '@/components/ui/ScreenEmpty';
import { formatNumber } from '@/utils/format';
import { CalculationMode } from '@bioavail/shared-types';

/**
 * Studies screen — manage saved sessions, multi-subject datasets, and exports.
 * Master-list (left) + detail (right).
 */
export function StudiesScreen() {
  const studies = useStore((s) => s.studies);
  const activeStudyId = useStore((s) => s.activeStudyId);
  const loadStudy = useStore((s) => s.loadStudy);
  const deleteStudy = useStore((s) => s.deleteStudy);
  const createStudy = useStore((s) => s.createStudy);
  const setActiveScreen = useStore((s) => s.setActiveScreen);
  const [selectedId, setSelectedId] = useState<string | null>(activeStudyId);

  const selected = studies.find((s) => s.id === selectedId) ?? studies[0];

  return (
    <div className="studies-screen">
      <div className="studies-list">
        <CollapsibleSection sectionKey="studies.sessions" title="Saved Sessions" column="left" defaultOpen>
          <Button variant="primary" size="sm" onClick={() => { createStudy({ name: 'Untitled study', mode: CalculationMode.Absolute }); }}>
            <Plus size={14} aria-hidden="true" /> New calculation
          </Button>
          <ul className="session-list">
            {studies.map((s) => (
              <li
                key={s.id}
                className={`session-item ${s.id === selected?.id ? 'active' : ''}`}
              >
                <button type="button" className="session-name" onClick={() => setSelectedId(s.id)}>
                  {s.name}
                </button>
                <span className="session-meta">
                  {s.arms.length} arms · {s.mode}
                </span>
                <div className="session-actions">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { loadStudy(s.id); setActiveScreen('calculator'); }}
                  >
                    Open
                  </Button>
                  {studies.length > 1 ? (
                    <Button size="sm" variant="ghost" onClick={() => { deleteStudy(s.id); setSelectedId(null); }}>
                      Delete
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </CollapsibleSection>
      </div>

      <div className="studies-detail">
        {selected ? (
          <>
            <DatasetSection />
            <ExportSection study={selected} />
            <AuditTrailSection />
          </>
        ) : (
          <ScreenEmpty
            title="No study selected"
            body="Create a new study to start entering data, or pick one from the list."
            primary={{
              label: 'New study',
              onClick: () => createStudy({ name: 'Untitled study', mode: CalculationMode.Absolute }),
            }}
          />
        )}
      </div>
    </div>
  );
}

function AuditTrailSection() {
  const trail = useStore((s) => s.trail);

  return (
    <CollapsibleSection sectionKey="studies.audit" title={`Audit Trail${trail.length > 0 ? ` (${trail.length})` : ''}`} column="right">
      {trail.length === 0 ? (
        <p className="field-hint">No audit entries yet. Computed results for this study will be logged here.</p>
      ) : (
        <ul className="audit-trail-list">
          {trail.slice(-20).reverse().map((entry) => (
            <li key={entry.id} className="audit-entry">
              <span className="mono">{new Date(entry.timestamp).toLocaleTimeString()}</span>
              {' · '}
              <span>F = {formatNumber(entry.resultSnapshot.fPercent, 4)}%</span>
              {entry.warnings.length > 0 ? <span> · {entry.warnings.length} warning(s)</span> : null}
            </li>
          ))}
        </ul>
      )}
    </CollapsibleSection>
  );
}

function DatasetSection() {
  const dataset = useStore((s) => s.dataset);

  return (
    <CollapsibleSection sectionKey="studies.datasets" title="Datasets & Cohort" column="right">
      {!dataset ? (
        <p className="empty-state">
          No multi-subject dataset loaded. Import a CSV with subject-level AUC and Cmax values
          to enable cohort summary and bioequivalence assessment (Phase 2).
        </p>
      ) : (
        <div className="flex-col-gap-2">
          <span className="field-label">{dataset.name}</span>
          <p className="field-hint">{dataset.subjects.length} subjects</p>
          <div className="table-scroll">
            <table className="generic-table">
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Treatment</th>
                  <th>Period</th>
                  <th>Sequence</th>
                  <th>AUC</th>
                  <th>Cmax</th>
                </tr>
              </thead>
              <tbody>
                {dataset.subjects.slice(0, 20).map((subj, i) => (
                  <tr key={i}>
                    <td>{subj.subjectId}</td>
                    <td>{subj.treatment}</td>
                    <td>{subj.period}</td>
                    <td>{subj.sequence}</td>
                    <td className="mono">{formatNumber(subj.auc, 4)}</td>
                    <td className="mono">{formatNumber(subj.cmax, 4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {dataset.subjects.length > 20 ? (
            <p className="field-hint">Showing first 20 of {dataset.subjects.length} subjects.</p>
          ) : null}
        </div>
      )}
    </CollapsibleSection>
  );
}

function ExportSection({ study }: { study: import('@/types/domain').Study }) {
  const pushToast = useStore((s) => s.pushToast);
  const output = useStore((s) => s.output);

  function exportCsv() {
    const lines: string[] = [];
    lines.push('arm,time,concentration,isBlq');
    for (const arm of study.arms) {
      for (const pt of arm.dataPoints) {
        lines.push(`${arm.label},${pt.time},${pt.concentration},${pt.isBlq}`);
      }
    }
    download(`${study.name}-data.csv`, lines.join('\n'), 'text/csv');
    pushToast('CSV exported.', 'info');
  }

  function exportPdf() {
    pushToast('PDF export is a Phase 2 feature.', 'warn');
  }

  function copyResult() {
    if (!output) {
      pushToast('No result to copy.', 'warn');
      return;
    }
    const text = `F = ${formatNumber(output.bioavailability.fPercent, 4)}% (${study.name})`;
    navigator.clipboard?.writeText(text);
    pushToast('Result copied to clipboard.', 'info');
  }

  return (
    <CollapsibleSection sectionKey="studies.export" title="Export & Reporting" column="right">
      <div className="flex-row-gap-2-wrap">
        <Button variant="secondary" size="sm" onClick={exportCsv}>
          <Download size={14} aria-hidden="true" /> CSV export
        </Button>
        <Button variant="secondary" size="sm" onClick={exportPdf}>
          <Download size={14} aria-hidden="true" /> PDF report (Phase 2)
        </Button>
        <Button variant="ghost" size="sm" onClick={copyResult}>
          <CopyIcon size={14} aria-hidden="true" /> Copy result
        </Button>
      </div>
    </CollapsibleSection>
  );
}

function download(filename: string, content: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
